import { StoreProvider, useStoreState } from 'easy-peasy';
import React, { useEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { history } from '~/history';
import { Routes } from '~/Routes';
import { store } from '~/store';
import { ApiContext } from './context';
import { api } from './api';
import Tracking from './tracking';
import Helmet from 'Components/Helmet';
import { ToastContainer, Slide } from 'react-toastify';
import Empty from 'Components/Empty';
import { IntlProvider } from 'react-intl';
import { LANGUAGES, useLocaleProvider } from './lang';
import Intercom from 'Components/Intercom';
import InactiveAccountPage from 'Pages/InactiveAccount';
import ErrorBoundary from 'Components/ErrorBoundary';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import duration from 'dayjs/plugin/duration';
import isMoment from 'dayjs/plugin/isMoment';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isMoment);
dayjs.extend(localizedFormat);

function App() {
  const { loading, user, solution } = useStoreState((state) => ({
    loading: state.auth.loading,
    user: state.auth.user,
    solution: state.auth.solution,
  }));

  const [favicon, setFavicon] = useState<string>();

  useEffect(() => {
    if (solution?.favicon) {
      setFavicon(solution.favicon);
    } else {
      setFavicon('/favicon.ico');
    }
    const brandColor = solution?.ui_settings?.lite?.fraiche_primary;
    if (brandColor) {
      document.documentElement.style.setProperty('--brand-color', brandColor);
    } else {
      document.documentElement.style.removeProperty('--brand-color');
    }
  }, [solution]);

  if (!loading && !solution && user) {
    return <InactiveAccountPage />;
  }

  return (
    <>
      {favicon && (
        <Helmet>
          <link rel="shortcut icon" href={favicon} />
        </Helmet>
      )}
      <ApiContext.Provider value={api}>
        {/* @ts-expect-error - Router v3 types don't match React 18 requirements */}
        <Router history={history}>
          {/* @ts-expect-error - Routes prop types need updating */}
          <Routes solution={solution} user={user} />
          <Tracking />
          <Empty />
        </Router>
      </ApiContext.Provider>
      {import.meta.env.VITE_APP_INTERCOM_APP_ID && <Intercom />}
    </>
  );
}

const IntlProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const { messages, locale } = useLocaleProvider();

  const reactIntlLocale =
    LANGUAGES[locale as keyof typeof LANGUAGES]?.reactIntlLocale || 'en-US';

  useEffect(() => {
    if (!locale || locale === 'en') {
      return;
    }
    (async () => {
      let thisLocale = locale;
      if (locale === 'zh') {
        thisLocale = 'cn';
      }
      const languageConfig = LANGUAGES[thisLocale as keyof typeof LANGUAGES];
      if (languageConfig) {
        await languageConfig.dayjsLocaleLoader();
        dayjs.locale((languageConfig as any).dayjsLocaleId || thisLocale);
      }
    })();
  }, [locale]);

  useEffect(() => {
    if (document.body.hasAttribute('data-localization-ready')) {
      return;
    }
    if (messages && Object.keys(messages).length > 0) {
      document.body.setAttribute('data-localization-ready', 'true');
    }
  }, [messages]);

  return (
    <IntlProvider
      messages={messages}
      locale={reactIntlLocale}
      defaultLocale="en-US"
      onError={(err) => {
        if (err.code === 'MISSING_TRANSLATION') return;
        if (err.code === 'MISSING_DATA') return;
        console.error(err);
      }}
    >
      {children}
    </IntlProvider>
  );
};

const AppWrapper = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <StoreProvider store={store}>
        <IntlProviderWrapper>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            transition={Slide}
          />
        </IntlProviderWrapper>
      </StoreProvider>
    </QueryClientProvider>
  );
};

export default AppWrapper;
